<script setup>

import {useForm} from "laravel-precognition-vue-inertia";
import AccountSettings from "../../components/ui-components/settings/AccountSettings.vue";
import EmailSettings from "../../components/ui-components/settings/EmailSettings.vue";
import SubscriptionSettings from "../../components/ui-components/settings/SubscriptionSettings.vue";
import Button from "primevue/button";
import Avatar from "primevue/avatar";
import {computed, ref} from "vue";
import {trans} from "laravel-vue-i18n";
import {usePage} from "@inertiajs/vue3";

const props = defineProps({
    smtp_server: String,
    smtp_port: String,
    smtp_username: String,
    smtp_password: String,
    smtp_encryption: String,
    smtp_address: String,
    smtp_name: String,
    user_name: String,
})

const tabs = computed(() => [
    {
        label: trans('settings.tab_account'),
        label_mobile: trans('settings.tab_account_short'),
        icon: 'pi pi-user',
        id: 'user',
    },
    {
        label: trans('settings.tab_subscription'),
        label_mobile: trans('settings.tab_subscription'),
        icon: 'pi pi-dollar',
        id: 'subscription',
    },
    {
        label: trans('settings.tab_email'),
        label_mobile: trans('settings.tab_email'),
        icon: 'pi pi-envelope',
        id: 'emails',
    },
]);

const form = useForm('post', '/app/settings', {
    smtp_server: props.smtp_server,
    smtp_port: props.smtp_port,
    smtp_username: props.smtp_username,
    smtp_password: props.smtp_password,
    smtp_encryption: props.smtp_encryption,
    smtp_address: props.smtp_address,
    smtp_name: props.smtp_name,
    // User account
    user_name: props.user_name,
    user_password: '',
    user_locale: usePage().props.locale,
});

function scrollToSection(id) {
    const el = document.getElementById(id);
    if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
}

import { onMounted, onBeforeUnmount } from "vue";
import FeatureBlocker from "../../components/ui-components/FeatureBlocker.vue";
const currentId = ref('user');

// Dynamically track which section is visible using IntersectionObserver
onMounted(() => {
    const sectionIds = ['user', 'subscription', 'emails'];
    const sections = sectionIds.map(id => document.getElementById(id)).filter(Boolean);
    let observer;
    if ('IntersectionObserver' in window && sections.length) {
        observer = new IntersectionObserver(
            (entries) => {
                // Find the entry that is most visible (largest intersectionRatio)
                let mostVisible = null;
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        if (
                            !mostVisible ||
                            entry.intersectionRatio > mostVisible.intersectionRatio
                        ) {
                            mostVisible = entry;
                        }
                    }
                }
                if (mostVisible) {
                    currentId.value = mostVisible.target.id;
                }
            },
            {
                root: null,
                rootMargin: '0px 0px -60% 0px', // Trigger a bit before the section is fully in view
                threshold: [0, 0.2, 0.5, 0.7, 1.0],
            }
        );
        sections.forEach(section => observer.observe(section));
    }
    // Cleanup observer on unmount
    onBeforeUnmount(() => {
        if (observer && sections.length) {
            sections.forEach(section => observer.unobserve(section));
            observer.disconnect();
        }
    });
});
</script>

<template>
    <div class="flex w-full relative gap-6">
        <!-- Sidebar -->
        <div class="w-64 max-lg:hidden shrink-0 -ml-1.5 overflow-visible">
            <ul class="flex flex-col gap-1 sticky top-28">
                <li
                    v-for="(item, index) in tabs"
                    :key="item.id"
                    @click="scrollToSection(item.id)"
                    :class="[
                        'flex items-center gap-5 px-4 py-2 rounded-xl cursor-pointer transition-all',
                        currentId === item.id
                          ? 'bg-neutral-200/20 text-roori-blue'
                          : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                    ]"
                >
                    <i
                        :class="[item.icon, currentId === item.id ? 'text-roori-blue' :  'text-neutral-400']"
                        class="!text-xl"
                    ></i>
                    <span class="font-medium">{{ item.label }}</span>
                </li>
            </ul>
        </div>
        <div class="lg:bg-white dark:lg:bg-black dark:border-neutral-700 max-lg:pt-12 dark:divide-neutral-700 lg:-mt-12 lg:border w-full lg:rounded-xl lg:shadow-sm p-4 lg:p-12 flex overflow-hidden divide-y flex-col">
            <div class="p-12 py-8 lg:bg-blue-50 dark:bg-blue-950 -m-12 mb-12 dark:border-neutral-700 border-b flex items-center gap-3">
                <Avatar :label="$page.props.user_initials" size="xlarge" shape="circle"></Avatar>
                <div>
                    <p class="text-xl font-bold">{{ $page.props.user.name }}</p>
                    <a :href="`${$page.props.olympus.account_url}/user/profile`" target="_blank"
                       class="text-sm text-gray-600 dark:text-gray-200 cursor-pointer">
                        {{ $t('settings.maeum_account') }} <span class="pi pi-arrow-up-right !text-xs"></span>
                    </a>
                </div>
            </div>
            <section id="user" class="flex gap-4 pb-12 !border-transparent max-lg:flex-col">
                <div class="w-72">
                    <p class="font-semibold text-lg">{{ $t('settings.section_personal') }}</p>
                    <p class="text-sm opacity-50">{{ $t('settings.section_personal_hint') }}</p>
                </div>
                <div>
                    <account-settings v-model="form"></account-settings>
                </div>
            </section>


            <section id="subscription" class="flex gap-4 py-12 max-lg:flex-col">
                <div class="min-w-72">
                    <p class="font-semibold text-lg">{{ $t('settings.section_subscription') }}</p>
                    <p class="text-sm opacity-50">{{ $t('settings.section_subscription_hint') }}</p>
                </div>
                <div class="w-full max-lg:mt-4 lg:pl-6">
                    <subscription-settings></subscription-settings>
                </div>
            </section>

            <FeatureBlocker name="roori_custom_email_settings" version="Pro">
                <section id="emails" class="flex gap-4 py-12 max-lg:flex-col">
                    <div class="min-w-72">
                        <p class="font-semibold text-lg">{{ $t('settings.section_email') }}</p>
                        <p class="text-sm opacity-50">{{ $t('settings.section_email_hint') }}</p>
                    </div>
                    <div>
                        <email-settings v-model="form"></email-settings>
                    </div>
                </section>
            </FeatureBlocker>
            <div class="!border-transparent max-lg:mb-8 mt-2">
                <Button :label="$t('common.save')"
                        :loading="form.processing"
                        @click="() => {
                        form.submit();
                    }">
                </Button>
            </div>
        </div>
    </div>
</template>


<style scoped lang="scss">

</style>
